import { inject, Injectable } from '@angular/core';
import { collectionCount, ORGANIZATION_ID } from '@context/frontend/common';
import { Fetchable, PaginatedObject } from '@context/frontend/pagination';
import { Activity, ActivityMap } from '@context/shared/types/activity';
import { Entity } from '@context/shared/types/common';
import { User } from '@context/shared/types/user';
import { sanitize } from '@context/shared/utils';
import {
	collection,
	doc,
	DocumentReference,
	DocumentSnapshot,
	getDocs,
	limit,
	query,
	setDoc,
	startAfter,
	Timestamp,
} from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ActivityService implements Fetchable<Activity> {
	private static readonly CollectionId = 'activity';

	protected readonly organizationId = inject(ORGANIZATION_ID);

	create(
		entityRef: DocumentReference<Entity>,
		activity: Pick<Activity, 'type' | 'data'>,
		creator: DocumentReference<User>,
	): Promise<Activity> {
		const organizationId = this.organizationId.value;
		if (!organizationId) throw new Error('Organization ID is required');

		const { type, data } = activity;
		const now = Timestamp.now();
		const payload: Activity = {
			...ActivityMap[type],
			event: 'user',
			user: creator,
			createdAt: now,
			updatedAt: now,
			data,
			id: crypto.randomUUID(),
			organizationId,
		};

		// the activity collection tied to the entity
		const activityCollection = collection(
			entityRef,
			ActivityService.CollectionId,
		);

		return setDoc(
			doc(activityCollection, payload.id),
			sanitize(payload),
		).then(() => payload);
	}

	async fetch(
		paginated?: PaginatedObject<Activity>,
	): Promise<PaginatedObject<Activity>> {
		if (!paginated) paginated = new PaginatedObject<Activity>();
		if (!paginated?.parentRef) throw new Error('Parent ref is required');

		const activityCollection = collection(
			paginated.parentRef,
			ActivityService.CollectionId,
		);

		if (!paginated.totalElements) {
			paginated.totalElements = await firstValueFrom(
				collectionCount(
					query(activityCollection, ...paginated.constraints),
				),
			);
		}

		// if the object already contains the data, don't fetch again
		if (paginated.page) return paginated;

		const constraints = [...paginated.constraints];
		const lastDoc = paginated.previousPage?.last ?? null;
		if (lastDoc) constraints.push(startAfter(lastDoc));
		if (paginated.pageSize) constraints.push(limit(paginated.pageSize));

		const snapshot = await getDocs(
			query(activityCollection, ...constraints),
		);

		let last: DocumentSnapshot | null = null;
		const items = snapshot.docs.map((d) => {
			last = d;
			return { data: d.data(), ref: d.ref, id: d.id };
		});

		paginated.pages = Object.assign({}, paginated.pages, {
			[paginated.currentPage as number]: { last, items },
		});

		return paginated;
	}
}
